import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { Invoice, InvoiceIssue, Quote, WorkOrder } from '@syndiqia/maintenance/contracts';
import { errorMessage, personaHeader, useApi, useAuthHeaders } from '../../api';
import { Alert, Field } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAccounts, useFunds } from '../../ledger-queries';
import { useMaintenanceMutation, useVendors } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { centsOf, dollars } from './MaintenanceNav';

// Quotes from invited contractors, and supplier invoices checked against the work order (board side).

/** Opens a protected file (invoice or quote) in a new tab. */
function useOpenFile() {
  const authHeaders = useAuthHeaders();
  return async (path: string) => {
    const tab = window.open('', '_blank');
    const res = await fetch(path, { headers: { ...authHeaders(), ...personaHeader(path) } });
    if (!res.ok) return tab?.close();
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
  };
}

/** Posts a multipart form (file and fields) to the API with the session's headers. */
function useUpload() {
  const authHeaders = useAuthHeaders();
  const queryClient = useQueryClient();
  const s = useSyndicate();
  return async <T,>(path: string, form: FormData): Promise<T> => {
    const res = await fetch(path, { method: 'POST', body: form, headers: { ...authHeaders(), ...personaHeader(path) } });
    const body = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
    if (!res.ok) throw Object.assign(new Error(body?.message ?? `HTTP ${res.status}`), { status: res.status, body });
    await queryClient.invalidateQueries({ queryKey: [s.tenantId, 'maintenance'] });
    return body as T;
  };
}

export function QuotesPanel({ order, manages }: { order: WorkOrder; manages: boolean }) {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const p = t.purchasing;
  const open = order.status !== 'completed' && order.status !== 'cancelled';
  const quotes = useQuery({ queryKey: [s.tenantId, 'maintenance', 'quotes', order.id], queryFn: () => api<Quote[]>(`${s.api}/maintenance/work-orders/${order.id}/quotes`) });
  const vendors = useVendors();
  const openFile = useOpenFile();
  const [inviting, setInviting] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [dueOn, setDueOn] = useState('');
  const invite = useMaintenanceMutation<{ vendorIds: string[]; dueOn?: string }, { invited: number }>((a, base, input) => a(`${base}/work-orders/${order.id}/quotes`, { method: 'POST', body: input }));
  const retain = useMaintenanceMutation<string, { linkSentTo: string | null }>((a, base, id) => a(`${base}/quotes/${id}/select`, { method: 'POST' }));

  const list = quotes.data ?? [];
  const canInvite = manages && open && !order.vendorId;
  if (!canInvite && list.length === 0) return null;
  const lowest = Math.min(...list.filter((q) => q.status === 'received' && q.amountCents).map((q) => q.amountCents!));
  const already = new Set(list.map((q) => q.vendorId));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (picked.length) invite.mutate({ vendorIds: picked, ...(dueOn ? { dueOn } : {}) }, { onSuccess: () => (setInviting(false), setPicked([])) });
  };

  return (
    <section className="panel">
      <h2>{p.quotes}</h2>
      {canInvite && <p className="muted small">{p.quotesHint}</p>}
      {list.length > 0 && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{p.vendor}</th>
              <th>{p.amount}</th>
              <th>{p.delay}</th>
              <th>{p.validUntil}</th>
              <th>{p.file}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((q) => (
              <tr key={q.id}>
                <td data-label={p.vendor}>
                  <strong>{q.vendorName}</strong> <span className={`badge badge-quote-${q.status}`}>{p.quoteStatuses[q.status]}</span>
                  {q.note && <div className="small muted">{q.note}</div>}
                </td>
                <td data-label={p.amount}>
                  {q.amountCents ? money(q.amountCents) : '—'}
                  {q.amountCents === lowest && list.filter((x) => x.status === 'received').length > 1 && <span className="small muted"> · {p.lowest}</span>}
                </td>
                <td data-label={p.delay}>{q.delayDays != null ? p.days(q.delayDays) : '—'}</td>
                <td data-label={p.validUntil}>{q.validUntil ? date(q.validUntil) : '—'}</td>
                <td data-label={p.file}>
                  {q.fileName ? (
                    <button type="button" className="link-button" onClick={() => void openFile(`${s.api}/maintenance/quotes/${q.id}/file`)}>
                      📎 {q.fileName}
                    </button>
                  ) : (
                    '—'
                  )}
                  {q.extracted?.exclusions.length ? (
                    <div className="small muted">
                      {p.exclusions} : {q.extracted.exclusions.join(' · ')}
                    </div>
                  ) : null}
                </td>
                <td>
                  {canInvite && q.status === 'received' && (
                    <button type="button" className="button button-small button-primary" disabled={retain.isPending} onClick={() => retain.mutate(q.id)}>
                      {p.retain}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {retain.isSuccess && <Alert tone="info">{p.retained(retain.data.linkSentTo)}</Alert>}
      {invite.isSuccess && !inviting && <Alert tone="info">{p.invited(invite.data.invited)}</Alert>}
      {(invite.isError || retain.isError) && <Alert>{errorMessage(invite.error ?? retain.error, t)}</Alert>}
      {canInvite && !inviting && (
        <div className="form-actions">
          <button type="button" className="button button-small" onClick={() => setInviting(true)}>
            {p.invite}
          </button>
        </div>
      )}
      {canInvite && inviting && (
        <form className="form" onSubmit={submit}>
          <div className="team-roles">
            {vendors.data
              ?.filter((v) => v.active)
              .map((v) => (
                <label key={v.id} className="team-role">
                  <input
                    type="checkbox"
                    disabled={!v.email || (!picked.includes(v.id) && picked.length >= 5)}
                    checked={picked.includes(v.id)}
                    onChange={(e) => setPicked((x) => (e.target.checked ? [...x, v.id] : x.filter((id) => id !== v.id)))}
                  />
                  <span>
                    {v.name} <span className="muted small">— {t.maintenance.trades[v.trade]}{!v.email ? ` (${p.noEmail})` : ''}{already.has(v.id) ? ' ✓' : ''}</span>
                  </span>
                </label>
              ))}
          </div>
          <Field label={p.dueOn} hint={t.common.optional}>
            <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </Field>
          <div className="form-actions">
            <button type="submit" className="button button-primary button-small" disabled={invite.isPending || picked.length === 0}>
              ✉ {p.inviteSend}
            </button>
            <button type="button" className="button button-small" onClick={() => setInviting(false)}>
              {t.common.cancel}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function InvoicesPanel({ order, manages }: { order: WorkOrder; manages: boolean }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const p = t.purchasing;
  const invoices = useQuery({ queryKey: [s.tenantId, 'maintenance', 'invoices', order.id], queryFn: () => api<Invoice[]>(`${s.api}/maintenance/work-orders/${order.id}/invoices`) });
  const [adding, setAdding] = useState(false);
  const list = invoices.data ?? [];
  if (!order.vendorId && list.length === 0) return null;
  if (!manages && list.length === 0) return null;

  return (
    <section className="panel">
      <h2>{p.invoices}</h2>
      <p className="muted small">
        {p.invoicesHint}
        {s.invoiceAddress && <> {p.invoiceAddressHint(s.invoiceAddress)}</>}
      </p>
      {list.map((i) => (
        <InvoiceCard key={i.id} invoice={i} manages={manages} />
      ))}
      {manages && order.status !== 'cancelled' && (adding ? <AddInvoice order={order} onDone={() => setAdding(false)} /> : (
        <div className="form-actions">
          <button type="button" className="button button-small" onClick={() => setAdding(true)}>
            {p.addInvoice}
          </button>
        </div>
      ))}
    </section>
  );
}

function IssueList({ issues }: { issues: InvoiceIssue[] }) {
  const { t, money } = useI18n();
  const p = t.purchasing;
  if (issues.length === 0) return <p className="small invoice-clean">✓ {p.clean}</p>;
  return (
    <ul className="invoice-issues small">
      {issues.map((x) => (
        <li key={x.code}>
          ⚠ {p.issues[x.code]}
          {x.amountCents != null && x.code === 'over_estimate' ? p.overBy(money(x.amountCents)) : ''}
          {x.amountCents != null && x.code === 'tax_arithmetic' ? p.expectedTaxes(money(x.amountCents)) : ''}
        </li>
      ))}
    </ul>
  );
}

export function InvoiceCard({ invoice: i, manages }: { invoice: Invoice; manages: boolean }) {
  const { t, date, money, locale } = useI18n();
  const s = useSyndicate();
  const p = t.purchasing;
  const openFile = useOpenFile();
  const accounts = useAccounts();
  const funds = useFunds();
  const [account, setAccount] = useState('5330');
  const [fund, setFund] = useState('OP');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState({ invoiceNumber: i.invoiceNumber, invoiceDate: i.invoiceDate ?? '', subtotal: dollars(i.subtotalCents), taxes: dollars(i.taxesCents), total: dollars(i.totalCents) });
  const approve = useMaintenanceMutation<{ accountNumber: string; fundCode: string }, Invoice>((a, base, input) => a(`${base}/invoices/${i.id}/approve`, { method: 'POST', body: input }));
  const reject = useMaintenanceMutation<string, Invoice>((a, base, note) => a(`${base}/invoices/${i.id}/reject`, { method: 'POST', body: { note } }));
  const save = useMaintenanceMutation<object, Invoice>((a, base, body) => a(`${base}/invoices/${i.id}`, { method: 'PATCH', body }));
  const expenseAccounts = (accounts.data ?? []).filter((a) => a.type === 'expense' && a.allowedFundKinds.includes(funds.data?.find((f) => f.code === fund)?.kind ?? 'operating'));
  const pending = i.status === 'received';
  const name = (x: { nameFr: string; nameEn: string }) => (locale === 'fr' ? x.nameFr : x.nameEn);

  return (
    <div className={`invoice-card invoice-${i.status}`}>
      <div className="invoice-head">
        <strong>
          {i.vendorName ?? '—'}
          {i.invoiceNumber ? ` · ${p.number} ${i.invoiceNumber}` : ''}
        </strong>
        <span className={`badge badge-invoice-${i.status}`}>{p.invoiceStatuses[i.status]}</span>
      </div>
      <p className="small muted">
        {i.invoiceDate ? `${date(i.invoiceDate)} · ` : ''}
        {p.source[i.source]}
        {i.fromEmail ? ` (${i.fromEmail})` : ''} · {date(i.receivedAt.slice(0, 10))}
        {i.matchedBy && i.matchedBy !== 'board' ? ` · ${p.matchedBy[i.matchedBy]}` : ''}
      </p>
      <p>
        {i.subtotalCents != null && (
          <>
            {p.subtotal} {money(i.subtotalCents)} · {p.taxes} {money(i.taxesCents ?? 0)} ·{' '}
          </>
        )}
        <strong>
          {p.total} {i.totalCents != null ? money(i.totalCents) : p.totalUnknown}
        </strong>{' '}
        <button type="button" className="link-button small" onClick={() => void openFile(`${s.api}/maintenance/invoices/${i.id}/file`)}>
          📎 {p.view}
        </button>
      </p>
      {pending && <IssueList issues={i.issues} />}
      {i.decisionNote && <p className="small muted">{i.decisionNote}</p>}

      {manages && pending && editing && (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(
              { invoiceNumber: fields.invoiceNumber, invoiceDate: fields.invoiceDate || null, subtotalCents: centsOf(fields.subtotal) ?? null, taxesCents: centsOf(fields.taxes) ?? null, totalCents: centsOf(fields.total) },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          <div className="row">
            <Field label={p.number}>
              <input value={fields.invoiceNumber} onChange={(e) => setFields({ ...fields, invoiceNumber: e.target.value })} />
            </Field>
            <Field label={p.date}>
              <input type="date" value={fields.invoiceDate} onChange={(e) => setFields({ ...fields, invoiceDate: e.target.value })} />
            </Field>
          </div>
          <div className="row">
            <Field label={p.subtotal}>
              <input inputMode="decimal" value={fields.subtotal} onChange={(e) => setFields({ ...fields, subtotal: e.target.value })} />
            </Field>
            <Field label={p.taxes}>
              <input inputMode="decimal" value={fields.taxes} onChange={(e) => setFields({ ...fields, taxes: e.target.value })} />
            </Field>
            <Field label={p.total}>
              <input inputMode="decimal" value={fields.total} onChange={(e) => setFields({ ...fields, total: e.target.value })} />
            </Field>
          </div>
          <div className="form-actions">
            <button type="submit" className="button button-small button-primary" disabled={save.isPending}>
              {p.save}
            </button>
            <button type="button" className="button button-small" onClick={() => setEditing(false)}>
              {t.common.cancel}
            </button>
          </div>
        </form>
      )}

      {manages && pending && !editing && !rejecting && (
        <div className="invoice-decide">
          <div className="row">
            <Field label={p.fund}>
              <select value={fund} onChange={(e) => setFund(e.target.value)}>
                {funds.data?.map((f) => (
                  <option key={f.code} value={f.code}>
                    {name(f)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={p.account}>
              <select value={account} onChange={(e) => setAccount(e.target.value)}>
                {expenseAccounts.map((a) => (
                  <option key={a.number} value={a.number}>
                    {a.number} — {name(a)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="form-actions">
            {s.can('ledger.entry.post') && (
              <button type="button" className="button button-small button-primary" disabled={approve.isPending || !expenseAccounts.some((a) => a.number === account)} onClick={() => approve.mutate({ accountNumber: account, fundCode: fund })}>
                ✓ {p.approve}
              </button>
            )}
            <button type="button" className="button button-small" onClick={() => setRejecting(true)}>
              {p.reject}
            </button>
            <button type="button" className="link-button small" onClick={() => setEditing(true)}>
              {p.edit}
            </button>
          </div>
        </div>
      )}
      {manages && pending && rejecting && (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length >= 3) reject.mutate(reason.trim());
          }}
        >
          <Field label={p.rejectReason}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          </Field>
          <div className="form-actions">
            <button type="submit" className="button button-small" disabled={reject.isPending || reason.trim().length < 3}>
              {p.reject}
            </button>
            <button type="button" className="button button-small" onClick={() => setRejecting(false)}>
              {t.common.cancel}
            </button>
          </div>
        </form>
      )}
      {(approve.isError || reject.isError || save.isError) && <Alert>{errorMessage(approve.error ?? reject.error ?? save.error, t)}</Alert>}
    </div>
  );
}

function AddInvoice({ order, onDone }: { order: WorkOrder; onDone: () => void }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const p = t.purchasing;
  const upload = useUpload();
  const [file, setFile] = useState<File | null>(null);
  const [fields, setFields] = useState({ invoiceNumber: '', subtotal: '', taxes: '', total: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v.trim()) form.append(k, v.trim());
    form.append('file', file);
    setBusy(true);
    setError(null);
    try {
      await upload(`${s.api}/maintenance/work-orders/${order.id}/invoices`, form);
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form" onSubmit={submit}>
      <Field label={p.addInvoice} hint={p.addInvoiceHint}>
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
      <div className="row">
        <Field label={p.number} hint={t.common.optional}>
          <input value={fields.invoiceNumber} onChange={(e) => setFields({ ...fields, invoiceNumber: e.target.value })} />
        </Field>
        <Field label={p.subtotal} hint={t.common.optional}>
          <input inputMode="decimal" value={fields.subtotal} onChange={(e) => setFields({ ...fields, subtotal: e.target.value })} />
        </Field>
        <Field label={p.taxes} hint={t.common.optional}>
          <input inputMode="decimal" value={fields.taxes} onChange={(e) => setFields({ ...fields, taxes: e.target.value })} />
        </Field>
        <Field label={p.total} hint={t.common.optional}>
          <input inputMode="decimal" value={fields.total} onChange={(e) => setFields({ ...fields, total: e.target.value })} />
        </Field>
      </div>
      {error ? <Alert>{errorMessage(error, t)}</Alert> : null}
      <div className="form-actions">
        <button type="submit" className="button button-primary button-small" disabled={busy || !file}>
          {p.addInvoice}
        </button>
        <button type="button" className="button button-small" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

/** Invoices received by email that could not be tied to a work order: the board sorts them (suggestions first). */
export function UnfiledInvoices() {
  const { t, money, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const p = t.purchasing;
  const openFile = useOpenFile();
  const invoices = useQuery({ queryKey: [s.tenantId, 'maintenance', 'invoices', 'unfiled'], queryFn: () => api<Invoice[]>(`${s.api}/maintenance/invoices?unfiled=true&status=received`) });
  const orders = useQuery({ queryKey: [s.tenantId, 'maintenance', 'work-orders', 'all', ''], queryFn: () => api<WorkOrder[]>(`${s.api}/maintenance/work-orders?status=all`) });
  const [choice, setChoice] = useState<Record<string, string>>({});
  const file = useMaintenanceMutation<{ id: string; workOrderId: string }, Invoice>((a, base, x) => a(`${base}/invoices/${x.id}/file`, { method: 'POST', body: { workOrderId: x.workOrderId } }));
  const reject = useMaintenanceMutation<string, Invoice>((a, base, id) => a(`${base}/invoices/${id}/reject`, { method: 'POST', body: { note: p.notInvoice } }));
  const list = invoices.data ?? [];
  if (list.length === 0) return null;
  const others = (i: Invoice) => (orders.data ?? []).filter((w) => w.status !== 'cancelled' && !i.suggestions.some((x) => x.workOrderId === w.id));

  return (
    <section className="panel">
      <h2>{p.toFileTitle(list.length)}</h2>
      <p className="muted small">{p.toFileHint}</p>
      {list.map((i) => (
        <div key={i.id} className="invoice-card">
          <div className="invoice-head">
            <strong>
              {i.vendorName ?? i.extracted?.vendorName ?? i.fromEmail ?? '—'}
              {i.invoiceNumber ? ` · ${p.number} ${i.invoiceNumber}` : ''}
            </strong>
            <span>{i.totalCents != null ? money(i.totalCents) : p.totalUnknown}</span>
          </div>
          <p className="small muted">
            {i.fromEmail} · {date(i.receivedAt.slice(0, 10))}
            {i.emailSubject ? ` · « ${i.emailSubject} »` : ''}{' '}
            <button type="button" className="link-button small" onClick={() => void openFile(`${s.api}/maintenance/invoices/${i.id}/file`)}>
              📎 {i.fileName}
            </button>
          </p>
          {s.can('maintenance.manage') && (
            <div className="inline-field">
              <select value={choice[i.id] ?? i.suggestions[0]?.workOrderId ?? ''} onChange={(e) => setChoice({ ...choice, [i.id]: e.target.value })} aria-label={p.workOrder}>
                <option value="">{p.chooseWorkOrder}</option>
                {i.suggestions.length > 0 && (
                  <optgroup label={p.suggested}>
                    {i.suggestions.map((x) => (
                      <option key={x.workOrderId} value={x.workOrderId}>
                        #{x.number} {x.title}
                        {x.vendorName ? ` — ${x.vendorName}` : ''} ({p.suggestionReasons[x.reason]})
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label={p.allWorkOrders}>
                  {others(i).map((w) => (
                    <option key={w.id} value={w.id}>
                      #{w.number} {w.title}
                      {w.vendorName ? ` — ${w.vendorName}` : ''}
                    </option>
                  ))}
                </optgroup>
              </select>
              <button
                type="button"
                className="button button-small button-primary"
                disabled={file.isPending || !(choice[i.id] ?? i.suggestions[0]?.workOrderId)}
                onClick={() => file.mutate({ id: i.id, workOrderId: (choice[i.id] ?? i.suggestions[0]?.workOrderId)! })}
              >
                {p.fileIt}
              </button>
              <button type="button" className="link-button small" disabled={reject.isPending} onClick={() => reject.mutate(i.id)}>
                {p.notInvoice}
              </button>
            </div>
          )}
        </div>
      ))}
      {(file.isError || reject.isError) && <Alert>{errorMessage(file.error ?? reject.error, t)}</Alert>}
    </section>
  );
}
